const dateFormatter = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });
const dateTimeFormatter = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" });

/** Format an ISO timestamp with the time, e.g. "Sep 6, 2026, 9:41 AM". */
export const formatDateTime = (value: string | null | undefined, fallback = "—"): string => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return dateTimeFormatter.format(date);
};

/** Format an ISO timestamp as e.g. "Sep 6, 2026". Returns the fallback when empty. */
export const formatDate = (value: string | null | undefined, fallback = "—"): string => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return dateFormatter.format(date);
};

/** Format a date-only value ("2026-07-01") as a calendar day, never shifted by the viewer's time zone. */
export const formatDay = (value: string | null | undefined, style: "medium" | "long" = "medium", fallback = "—"): string => {
  if (!value) return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return formatDate(value, fallback);
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return style === "long" ? date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : dateFormatter.format(date);
};

/**
 * POL-13: a calendar day in the client's own display setting (epic SET wrote it;
 * `client.settings.dateDisplay` carries it, "us" or "iso"). `useDay()` in `useDay.ts`
 * is how a screen reaches it without passing the client through every component.
 *
 * An ISO date in a sentence reads like a machine wrote it, which on the Review screen
 * it did - but a client who asked for ISO gets ISO, including in the sentence.
 */
export const formatDayAs = (
  dateDisplay: string | null | undefined,
  value: string | null | undefined,
  style: "medium" | "long" = "medium",
  fallback = "—"
): string => {
  if (!value) return fallback;
  if (dateDisplay === "iso") return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : formatDay(value, style, fallback);
  return formatDay(value, style, fallback);
};

/** Display name for a user: name when present, otherwise the email. */
export const displayName = (user: { name: string | null; email: string }): string =>
  user.name?.trim() || user.email;

/** Derive a URL slug from a free-text name. */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
